import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Store,
  Users,
  Megaphone,
  Code,
  DollarSign,
  Palette,
  Shield,
  Target,
  User,
  Loader2,
  CheckCircle,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
export default function BizRegist() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [selectedRoles, setSelectedRoles] = useState(["CEO"]);
  const [businessName, setBusinessName] = useState("");
  const [businessContent, setBusinessContent] = useState("");
  const [notes, setNotes] = useState("");
  const [capital, setCapital] = useState("");
  const [marketSize, setMarketSize] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");

  const roles = [
    {
      id: "sales",
      name: "세일즈 전문가",
      icon: Store,
      role: "시장 진입 전략과 고객 확보 방안에 대해 현실적인 피드백을 제공합니다.",
      color: "from-blue-500 to-blue-600",
    },
    {
      id: "hr",
      name: "인재 관리자",
      icon: Users,
      role: "필요 인력과 조직 구성의 실현 가능성을 검토합니다.",
      color: "from-purple-500 to-purple-600",
    },
    {
      id: "marketing",
      name: "브랜드 전략가",
      icon: Megaphone,
      role: "마케팅 전략과 브랜드 포지셔닝의 효과성을 비판적으로 평가합니다.",
      color: "from-pink-500 to-pink-600",
    },
    {
      id: "dev",
      name: "기술 리더",
      icon: Code,
      role: "기술 구현의 난이도와 개발 일정의 현실성을 검증합니다.",
      color: "from-green-500 to-green-600",
    },
    {
      id: "finance",
      name: "재무 분석가",
      icon: DollarSign,
      role: "자금 조달과 수익 모델의 지속가능성을 냉정하게 분석합니다.",
      color: "from-yellow-500 to-yellow-600",
    },
    {
      id: "design",
      name: "경험 디자이너",
      icon: Palette,
      role: "사용자 경험과 디자인의 시장 경쟁력을 평가합니다.",
      color: "from-indigo-500 to-indigo-600",
    },
    {
      id: "legal",
      name: "리스크 관리자",
      icon: Shield,
      role: "법적 규제와 컴플라이언스 리스크를 사전에 검토합니다.",
      color: "from-red-500 to-red-600",
    },
    {
      id: "product",
      name: "제품 전략가",
      icon: Target,
      role: "제품의 시장 적합성과 경쟁 우위를 객관적으로 분석합니다.",
      color: "from-orange-500 to-orange-600",
    },
  ];

  const toggleRole = (roleName) => {
    if (roleName === "CEO") return;

    setSelectedRoles((prev) =>
      prev.includes(roleName)
        ? prev.filter((id) => id !== roleName)
        : [...prev, roleName]
    );
  };

  const handleSubmit = async () => {
    // 유효성 검사
    if (!businessName || !businessContent) {
      setError("비즈니스 이름과 내용을 모두 입력해주세요.");
      return;
    }
    if (!capital || !marketSize || !notes) {
      setError("참고 사항을 모두 입력해주세요.");
      return;
    }
    if (businessContent.length > 500) {
      setError("비즈니스 내용은 500자 이내로 작성해주세요.");
      return;
    }

    setError("");
    setIsLoading(true);

    const requestData = {
      name: businessName,
      content: businessContent,
      notes: notes || undefined,
      capital: capital || undefined,
      marketSize: marketSize || undefined,
      selectedRoles: selectedRoles.filter((role) => role !== "CEO"), // CEO 제외
    };

    await api.post("/simulator/run", requestData);
    setTimeout(() => {
      setIsLoading(false);
      setShowSuccess(true);
    }, 3000);
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="mb-8 flex justify-center">
            <CheckCircle className="w-24 h-24 text-teal-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            가상의 회사를 설립중입니다
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            비즈니스 레포트에서 사업 진행 상황을 실시간으로 확인할 수 있습니다.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-300"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-teal-500 animate-spin mx-auto mb-4" />
          <p className="text-lg text-gray-600">비즈니스를 분석중입니다...</p>
          <p className="text-sm text-gray-500 mt-2">
            전문가들이 당신의 비즈니스를 검토하고 있습니다...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="group flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 text-white hover:bg-white/30 transition-all duration-300"
            >
              <ArrowLeft className="w-4 h-4 group-hover:transform group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-medium">뒤로가기</span>
            </button>
            <h1 className="text-2xl font-bold text-white">비즈니스 실현하기</h1>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Main Content - Horizontal Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Side - Input Fields */}
          <div className="space-y-6">
            {/* Business Info Card */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="bg-gradient-to-r from-teal-500 to-cyan-600 p-4">
                <h2 className="text-xl font-bold text-white">비즈니스 정보</h2>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <Target className="w-4 h-4 text-teal-600" />
                    비즈니스 이름
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    placeholder="예: 에코 스마트 팜"
                    maxLength={100}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    고객에게 노출될 비즈니스의 공식 명칭입니다
                  </p>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <Megaphone className="w-4 h-4 text-teal-600" />
                    비즈니스 내용
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={businessContent}
                    onChange={(e) => setBusinessContent(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all resize-none"
                    rows={4}
                    placeholder="해결하고자 하는 문제, 제공할 가치, 타겟 고객 등을 구체적으로 설명해주세요"
                    maxLength={500}
                  />
                  <div className="flex justify-between mt-1">
                    <p className="text-xs text-gray-500">
                      비즈니스 모델과 핵심 가치를 명확히 작성해주세요
                    </p>
                    <span
                      className={`text-xs ${
                        businessContent.length > 500
                          ? "text-red-500"
                          : "text-gray-400"
                      }`}
                    >
                      {businessContent.length}/500
                    </span>
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <DollarSign className="w-4 h-4 text-teal-600" />
                    참고사항
                  </label>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <input
                          type="text"
                          value={capital}
                          onChange={(e) => setCapital(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 focus:border-teal-500 text-sm"
                          placeholder="초기 자본금 (예: 5천만원)"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          value={marketSize}
                          onChange={(e) => setMarketSize(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 focus:border-teal-500 text-sm"
                          placeholder="목표 시장 규모"
                        />
                      </div>
                    </div>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 focus:border-teal-500 text-sm resize-none"
                      rows={2}
                      placeholder="보유 인맥, 기존 고객사, 특별한 강점, 관련 경험 등 AI가 참고할 추가 정보"
                      maxLength={300}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    더 정확한 분석을 위해 구체적인 정보를 제공해주세요
                  </p>
                </div>

                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                  <p className="text-xs text-teal-700">
                    💡 <span className="font-semibold">Tip:</span> 구체적이고
                    현실적인 정보를 입력할수록 더 정확한 비즈니스 분석을 받을 수
                    있습니다.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button for Mobile */}
            <button
              onClick={handleSubmit}
              disabled={isLoading || selectedRoles.length - 1 === 0}
              className="lg:hidden w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              가상의 비즈니스 실현하기
            </button>
          </div>

          {/* Right Side - Role Selection */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              팀 구성원 선택
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              CEO는 모든 업무를 담당하며, 선택한 전문가들이 피드백을 제공합니다.
            </p>

            {/* CEO Role (Always Selected) */}
            <div className="mb-4">
              <div className="bg-gradient-to-r from-teal-500 to-cyan-600 rounded-lg p-3 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="w-6 h-6" />
                    <div>
                      <h3 className="font-semibold text-sm">CEO (필수)</h3>
                      <p className="text-xs text-teal-100">모든 업무 총괄</p>
                    </div>
                  </div>
                  <CheckCircle className="w-5 h-5" />
                </div>
              </div>
            </div>

            {/* Other Roles - Grid Layout */}
            <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
              {roles.map((role) => (
                <button
                  key={role.id}
                  onClick={() => toggleRole(role.name)}
                  className={`group relative overflow-hidden rounded-lg p-3 border transition-all duration-300 ${
                    selectedRoles.includes(role.name)
                      ? "border-teal-500 bg-teal-50"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`p-1.5 rounded-md bg-gradient-to-r ${role.color} flex-shrink-0`}
                    >
                      <role.icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <h3 className="font-medium text-sm text-gray-900 truncate">
                        {role.name}
                      </h3>
                    </div>
                  </div>
                  {selectedRoles.includes(role.id) && (
                    <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-teal-500" />
                  )}
                </button>
              ))}
            </div>

            {/* Selected count */}
            <p className="text-xs text-gray-500 mt-3">
              {selectedRoles.length - 1}명의 전문가 선택됨
            </p>

            {/* Desktop Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={isLoading || selectedRoles.length - 1 === 0}
              className="hidden lg:block w-full mt-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl font-bold hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              가상의 비즈니스 실현하기
            </button>
          </div>
        </div>

        {/* Selected Roles Description */}
        {selectedRoles.length > 1 && (
          <div className="mt-6 bg-white rounded-xl shadow-md p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              선택된 전문가의 역할
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {roles
                .filter((role) => selectedRoles.includes(role.name))
                .map((role) => (
                  <div key={role.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <div
                        className={`p-1.5 rounded-md bg-gradient-to-r ${role.color} flex-shrink-0`}
                      >
                        <role.icon className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium text-sm text-gray-900">
                          {role.name}
                        </h4>
                        <p className="text-xs text-gray-600 mt-1">
                          {role.role}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
